import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowsClockwise, CheckCircle, HandCoins, Users, Calendar, Hammer, CaretRight, Receipt, Coins } from "@phosphor-icons/react";

const TABS = [
    { id: "payroll", label: "Payroll", icon: HandCoins, route: "/payroll-approvals" },
    { id: "contract_payouts", label: "Contract payouts", icon: Hammer, route: "/contract-payouts" },
    { id: "advances", label: "Advances", icon: Users, route: "/employee-advances" },
    { id: "expense_claims", label: "Expense claims", icon: Receipt, route: "/expense-claims" },
    { id: "petty_cash", label: "Petty cash", icon: Coins, route: "/petty-cash" },
    { id: "leaves", label: "Leaves", icon: Calendar, route: "/leaves" },
];

export default function AllApprovals() {
    const [queue, setQueue] = useState({ payroll: [], contract_payouts: [], advances: [], leaves: [], expense_claims: [], petty_cash: [], counts: { total: 0 } });
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState("payroll");
    const navigate = useNavigate();

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/approvals/queue");
            setQueue(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const counts = queue.counts || {};

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="All approvals"
                sub="Every pending approval across the system, in one place. Click a tab to review or open the full page for detailed actions."
                right={
                    <Button variant="outline" onClick={refresh} disabled={loading} data-testid="aa-refresh">
                        <ArrowsClockwise size={14} className="mr-2" /> Refresh
                    </Button>
                } />

            <div className="p-8 space-y-6">
                {/* Summary strip */}
                <div className="grid grid-cols-7 gap-3">
                    <Card label="Total pending" value={counts.total || 0} tone="teal" bold big />
                    {TABS.map((t) => (
                        <Card key={t.id} label={t.label} value={counts[t.id] || 0} icon={t.icon}
                              active={active === t.id} onClick={() => setActive(t.id)} testid={`aa-card-${t.id}`} />
                    ))}
                </div>

                {/* Tabs */}
                <div className="flex border-b" style={{ borderColor: "var(--se-border)" }}>
                    {TABS.map((t) => {
                        const c = counts[t.id] || 0;
                        return (
                            <button key={t.id} onClick={() => setActive(t.id)}
                                    className={`px-5 py-2.5 text-sm border-b-2 -mb-px ${active === t.id ? "border-orange-500 text-orange-600 font-medium" : "border-transparent text-stone-600 hover:text-stone-900"}`}
                                    data-testid={`aa-tab-${t.id}`}>
                                <t.icon size={14} className="inline mr-2" />
                                {t.label}
                                {c > 0 ? <span className="ml-2 text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{c}</span> : null}
                            </button>
                        );
                    })}
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => navigate(TABS.find((t) => t.id === active).route)}
                            data-testid="aa-open-full">
                        Open full page <CaretRight size={12} className="ml-1" />
                    </Button>
                </div>

                {/* Body */}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    {active === "payroll" ? <PayrollSection rows={queue.payroll} onDone={refresh} /> : null}
                    {active === "contract_payouts" ? <ContractPayoutSection rows={queue.contract_payouts} onDone={refresh} /> : null}
                    {active === "advances" ? <AdvancesSection rows={queue.advances} onDone={refresh} navigate={navigate} /> : null}
                    {active === "expense_claims" ? <ExpenseClaimsSection rows={queue.expense_claims} onDone={refresh} navigate={navigate} /> : null}
                    {active === "petty_cash" ? <PettyCashSection rows={queue.petty_cash} bills={queue.petty_cash_bills} onDone={refresh} navigate={navigate} /> : null}
                    {active === "leaves" ? <LeavesSection rows={queue.leaves} onDone={refresh} navigate={navigate} /> : null}
                </div>
            </div>
        </>
    );
}

function PayrollSection({ rows, onDone }) {
    const total = useMemo(() => rows.reduce((s, r) => s + (r.net_payable || 0), 0), [rows]);
    const approveAll = async () => {
        if (!rows.length) return;
        if (!window.confirm(`Approve all ${rows.length} payroll drafts?\nTotal net payable: ₹${inr(total)}`)) return;
        try {
            const { data } = await api.post("/payroll/runs/bulk-approve", { ids: rows.map((r) => r.id) });
            toast.success(`Approved ${data.approved?.length || 0} · Failed ${data.failed?.length || 0}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    if (!rows.length) return <Empty text="No payroll drafts waiting for approval." />;
    return (
        <>
            <SectionHeader
                title={`Payroll drafts — ${rows.length} pending · ₹ ${inr(total)} total net`}
                right={<Button size="sm" onClick={approveAll} style={{ background: "#16a34a", color: "#fff" }} data-testid="aa-payroll-approve-all">
                    <CheckCircle size={13} className="mr-1" /> Approve all
                </Button>}
            />
            <table className="se-table" data-testid="aa-payroll-table">
                <thead><tr><th>Employee</th><th>Month</th><th>Drafted by</th><th className="text-right">OT</th><th className="text-right">Incentive</th><th className="text-right">Penalty</th><th className="text-right">Net payable</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`aa-payroll-row-${r.id}`}>
                            <td>{r.employee_name}<div className="text-[10px] text-stone-500">{r.employee_code}</div></td>
                            <td>{r.month}</td>
                            <td className="text-xs">{r.drafted_by_name || "—"}<div className="text-[10px] text-stone-500 capitalize">{r.drafted_by_role || ""}</div></td>
                            <td className="se-num text-right">{r.ot_hours || 0}h · ₹{inr(r.ot_amount)}</td>
                            <td className="se-num text-right">{r.incentive > 0 ? `+₹${inr(r.incentive)}` : "—"}</td>
                            <td className="se-num text-right">{r.penalty > 0 ? `−₹${inr(r.penalty)}` : "—"}</td>
                            <td className="se-num text-right font-medium" style={{ color: "#0f766e" }}>₹ {inr(r.net_payable)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

function ContractPayoutSection({ rows, onDone }) {
    const total = useMemo(() => rows.reduce((s, r) => s + (r.net_payable || 0), 0), [rows]);
    const approveOne = async (rid) => {
        try {
            await api.post(`/contract-payouts/runs/${rid}/approve`);
            toast.success("Approved");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    if (!rows.length) return <Empty text="No contract-payout drafts waiting for approval." />;
    return (
        <>
            <SectionHeader title={`Contract payouts — ${rows.length} pending · ₹ ${inr(total)} total net`} />
            <table className="se-table" data-testid="aa-cp-table">
                <thead><tr><th>Worker</th><th>Month</th><th>Drafted by</th><th className="text-right">Gross</th><th className="text-right">TDS</th><th className="text-right">Net</th><th></th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`aa-cp-row-${r.id}`}>
                            <td>{r.worker_name}<div className="text-[10px] text-stone-500">{r.worker_code} · {r.entity_type}</div></td>
                            <td>{r.month}</td>
                            <td className="text-xs">{r.drafted_by_name || "—"}<div className="text-[10px] text-stone-500 capitalize">{r.drafted_by_role || ""}</div></td>
                            <td className="se-num text-right">₹ {inr(r.gross_amount)}</td>
                            <td className="se-num text-right">₹ {inr(r.tds_amount)}</td>
                            <td className="se-num text-right font-medium" style={{ color: "#0f766e" }}>₹ {inr(r.net_payable)}</td>
                            <td className="text-right">
                                <Button size="sm" onClick={() => approveOne(r.id)}
                                        style={{ background: "#16a34a", color: "#fff" }}
                                        data-testid={`aa-cp-approve-${r.id}`}>Approve</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

function AdvancesSection({ rows, onDone, navigate }) {
    const total = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
    if (!rows.length) return <Empty text="No employee advance requests waiting for approval." />;
    return (
        <>
            <SectionHeader title={`Employee advances — ${rows.length} pending · ₹ ${inr(total)} total`}
                          right={<span className="text-[11px] text-stone-500">Advances need EMI setup — approve on the full page.</span>} />
            <table className="se-table" data-testid="aa-adv-table">
                <thead><tr><th>Employee</th><th>Requested by</th><th className="text-right">Amount</th><th>Reason</th><th>Requested</th><th></th></tr></thead>
                <tbody>
                    {rows.map((a) => (
                        <tr key={a.id} data-testid={`aa-adv-row-${a.id}`}>
                            <td>{a.employee_name}<div className="text-[10px] text-stone-500">{a.employee_code}</div></td>
                            <td className="text-xs">{a.requested_by_name || "—"}<div className="text-[10px] text-stone-500 capitalize">{a.requested_by_role || ""}</div></td>
                            <td className="se-num text-right font-medium">₹ {inr(a.amount)}</td>
                            <td className="text-xs max-w-md truncate" title={a.reason}>{a.reason}</td>
                            <td className="text-xs">{formatDate(a.created_at)}</td>
                            <td className="text-right">
                                <Button size="sm" variant="outline"
                                        onClick={() => navigate("/employee-advances")}
                                        data-testid={`aa-adv-review-${a.id}`}>
                                    Review <CaretRight size={12} className="ml-1" />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

function ExpenseClaimsSection({ rows, onDone, navigate }) {
    const total = useMemo(() => rows.reduce((s, r) => s + (r.total_amount || 0), 0), [rows]);
    const approveOne = async (cid) => {
        try {
            await api.post(`/expense-claims/${cid}/approve`);
            toast.success("Approved — accountant can now reimburse");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const rejectOne = async (cid) => {
        const reason = window.prompt("Reason for rejection?");
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/expense-claims/${cid}/reject`, { reason: reason.trim() });
            toast.success("Rejected — claimant will see the reason");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    if (!rows.length) return <Empty text="No expense claims waiting for approval." />;
    return (
        <>
            <SectionHeader title={`Expense claims — ${rows.length} pending · ₹ ${inr(total)} total`} />
            <table className="se-table" data-testid="aa-ec-table">
                <thead><tr><th>Claim #</th><th>Claimant</th><th className="text-right">Amount</th><th>Reason</th><th>Submitted</th><th></th></tr></thead>
                <tbody>
                    {rows.map((c) => (
                        <tr key={c.id} data-testid={`aa-ec-row-${c.id}`}>
                            <td className="font-mono text-xs">{c.claim_no}</td>
                            <td>{c.claimant_name}<RoleBadge role={c.claimant_role} /></td>
                            <td className="se-num text-right font-medium">₹ {inr(c.total_amount)}</td>
                            <td className="text-xs max-w-md truncate" title={c.reason}>{c.reason}</td>
                            <td className="text-xs">{formatDate(c.submitted_at || c.created_at)}</td>
                            <td className="text-right space-x-1">
                                <Button size="sm"
                                        onClick={() => navigate(`/expense-claims?open=${c.id}`)}
                                        style={{ background: "#f59e0b", color: "#fff" }}
                                        data-testid={`aa-ec-review-${c.id}`}>
                                    Review & classify →
                                </Button>
                                <Button size="sm" onClick={() => rejectOne(c.id)}
                                        className="text-rose-600 hover:bg-rose-50"
                                        variant="outline"
                                        data-testid={`aa-ec-reject-${c.id}`}>Reject</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

function PettyCashSection({ rows, bills, onDone, navigate }) {
    const total = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
    const billTotal = useMemo(() => (bills || []).reduce((s, b) => s + (b.amount || 0), 0), [bills]);
    const approveOne = async (rid) => {
        try {
            await api.post(`/petty-cash/requests/${rid}/approve`);
            toast.success("Approved — no money has moved yet", {
                description: "Go to Petty Cash → \"To disburse\" tab to book the cash-out journal.",
                action: {
                    label: "Open Petty Cash",
                    onClick: () => navigate("/petty-cash"),
                },
                duration: 8000,
            });
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const rejectOne = async (rid) => {
        const reason = window.prompt("Reason for rejection?");
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/petty-cash/requests/${rid}/reject`, { reason: reason.trim() });
            toast.success("Rejected");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    if (!rows.length && !(bills || []).length) return <Empty text="No petty-cash requests or bill submissions waiting for approval." />;
    return (
        <>
            {(bills || []).length > 0 ? (
                <>
                    <SectionHeader
                        title={`Petty cash bills — ${bills.length} awaiting classification · ₹ ${inr(billTotal)}`}
                        right={<span className="text-[11px] text-stone-500">Open <b>Petty Cash → Approvals</b> to classify each bill (Tier 1 / 2 / 3) and post the journal.</span>}
                    />
                    <table className="se-table mb-4" data-testid="aa-pc-bills-table">
                        <thead><tr><th>Submitter</th><th className="text-right">Amount</th><th>Note</th><th>Bill</th><th>Filed</th><th></th></tr></thead>
                        <tbody>
                            {bills.map((b) => (
                                <tr key={b.id} data-testid={`aa-pc-bill-${b.id}`}>
                                    <td>{b.employee_name}<RoleBadge role={b.submitter_role} /></td>
                                    <td className="se-num text-right font-medium">₹ {inr(b.amount)}</td>
                                    <td className="text-xs max-w-md truncate">{b.note || "—"}</td>
                                    <td className="text-xs">{b.document_id ? "attached" : <span className="text-rose-600">missing</span>}</td>
                                    <td className="text-xs">{formatDate(b.created_at)}</td>
                                    <td className="text-right">
                                        <Button size="sm" onClick={() => navigate("/petty-cash?tab=approvals")}
                                                style={{ background: "#f59e0b", color: "#fff" }}
                                                data-testid={`aa-pc-bill-review-${b.id}`}>
                                            Review →
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            ) : null}
            {rows.length > 0 ? (
                <><SectionHeader
                title={`Petty cash — ${rows.length} pending · ₹ ${inr(total)} total`}
                right={<span className="text-[11px] text-stone-500">Approve here → then <b>Petty Cash → To disburse</b> to actually pay out.</span>}
            />
            <table className="se-table" data-testid="aa-pc-table">
                <thead><tr><th>Employee</th><th className="text-right">Amount</th><th>Reason</th><th className="text-right">In-hand at request</th><th>Requested</th><th></th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`aa-pc-row-${r.id}`}>
                            <td>{r.employee_name}<div className="text-[10px] text-stone-500 capitalize">{r.employee_role || ""}</div></td>
                            <td className="se-num text-right font-medium">₹ {inr(r.amount)}</td>
                            <td className="text-xs max-w-md truncate" title={r.reason}>{r.reason}</td>
                            <td className="se-num text-right text-xs text-stone-600">₹ {inr(r.in_hand_at_request || 0)} <span className="text-[10px] text-stone-400">/ cap ₹{inr(r.cap_at_request || 0)}</span></td>
                            <td className="text-xs">{formatDate(r.created_at)}</td>
                            <td className="text-right space-x-1">
                                <Button size="sm" variant="outline"
                                        onClick={() => navigate("/petty-cash")}
                                        data-testid={`aa-pc-view-${r.id}`}>View</Button>
                                <Button size="sm" onClick={() => rejectOne(r.id)}
                                        className="text-rose-600 hover:bg-rose-50"
                                        variant="outline"
                                        data-testid={`aa-pc-reject-${r.id}`}>Reject</Button>
                                <Button size="sm" onClick={() => approveOne(r.id)}
                                        style={{ background: "#16a34a", color: "#fff" }}
                                        data-testid={`aa-pc-approve-${r.id}`}>Approve</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table></>
            ) : null}
        </>
    );
}

function LeavesSection({ rows, onDone, navigate }) {
    if (!rows.length) return <Empty text="No leave requests waiting for approval." />;
    return (
        <>
            <SectionHeader title={`Leave requests — ${rows.length} pending`} />
            <table className="se-table" data-testid="aa-lv-table">
                <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th className="text-right">Days</th><th>Reason</th><th></th></tr></thead>
                <tbody>
                    {rows.map((lv) => (
                        <tr key={lv.id} data-testid={`aa-lv-row-${lv.id}`}>
                            <td>{lv.employee_name}<div className="text-[10px] text-stone-500">{lv.employee_code}</div></td>
                            <td className="uppercase text-xs">{lv.leave_type}</td>
                            <td className="text-xs">{lv.from_date}</td>
                            <td className="text-xs">{lv.to_date}</td>
                            <td className="se-num text-right">{lv.total_days}</td>
                            <td className="text-xs max-w-xs truncate" title={lv.reason}>{lv.reason || "—"}</td>
                            <td className="text-right">
                                <Button size="sm" variant="outline"
                                        onClick={() => navigate("/leaves")}
                                        data-testid={`aa-lv-review-${lv.id}`}>
                                    Review <CaretRight size={12} className="ml-1" />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

function SectionHeader({ title, right }) {
    return (
        <div className="px-5 py-3 border-b flex items-center gap-3" style={{ borderColor: "var(--se-border)" }}>
            <div className="font-serif text-lg mr-auto">{title}</div>
            {right}
        </div>
    );
}

function Empty({ text }) {
    return <div className="py-16 text-center text-stone-500 text-sm">{text}</div>;
}

function RoleBadge({ role }) {
    if (!role) return null;
    const r = String(role).toLowerCase();
    const palette = {
        founder:    { bg: "#f5f3ff", fg: "#6d28d9", border: "#ddd6fe" },
        accountant: { bg: "#eef2ff", fg: "#4338ca", border: "#c7d2fe" },
    };
    const p = palette[r] || { bg: "#f5f5f4", fg: "#57534e", border: "#e7e5e4" };
    return (
        <div className="mt-0.5">
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                  style={{ background: p.bg, color: p.fg, borderColor: p.border }}
                  data-testid={`role-badge-${r}`}>{r}</span>
        </div>
    );
}

function Card({ label, value, icon: Icon, tone, bold, big, active, onClick, testid }) {
    const border = active ? "#f97316" : "var(--se-border)";
    const bg = active ? "#fff7ed" : "#fff";
    const color = tone === "teal" ? "#0f766e" : "#0f172a";
    return (
        <button onClick={onClick} data-testid={testid}
                className="border rounded-md px-4 py-3 text-left transition-colors hover:bg-stone-50"
                style={{ borderColor: border, background: bg, cursor: onClick ? "pointer" : "default" }}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">
                {Icon ? <Icon size={13} /> : null} {label}
            </div>
            <div className={`se-num mt-1 ${big ? "text-3xl" : "text-2xl"} ${bold ? "font-medium" : ""}`} style={{ color }}>
                {value}
            </div>
        </button>
    );
}
